/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { ClosedEnum } from "../../types/enums.js";
import {
  AssistantMCPServerRef,
  AssistantMCPServerRef$Outbound,
  AssistantMCPServerRef$outboundSchema,
} from "./assistantmcpserverref.js";
import {
  AssistantToolsetRef,
  AssistantToolsetRef$Outbound,
  AssistantToolsetRef$outboundSchema,
} from "./assistanttoolsetref.js";

/**
 * The runtime version read before requesting a switch. Required with runtime_version; conflicts require a fresh read.
 */
export const ExpectedRuntimeVersion = {
  Two: 2,
  Three: 3,
} as const;
/**
 * The runtime version read before requesting a switch. Required with runtime_version; conflicts require a fresh read.
 */
export type ExpectedRuntimeVersion = ClosedEnum<typeof ExpectedRuntimeVersion>;

/**
 * Desired runtime protocol: 2 for the default runtime, 3 for Kit. Switching requires the assistant and all background work to be settled.
 */
export const UpdateAssistantFormRuntimeVersion = {
  Two: 2,
  Three: 3,
} as const;
/**
 * Desired runtime protocol: 2 for the default runtime, 3 for Kit. Switching requires the assistant and all background work to be settled.
 */
export type UpdateAssistantFormRuntimeVersion = ClosedEnum<
  typeof UpdateAssistantFormRuntimeVersion
>;

/**
 * The assistant status.
 */
export const UpdateAssistantFormStatus = {
  Active: "active",
  Paused: "paused",
} as const;
/**
 * The assistant status.
 */
export type UpdateAssistantFormStatus = ClosedEnum<
  typeof UpdateAssistantFormStatus
>;

export type UpdateAssistantForm = {
  /**
   * The runtime version read before requesting a switch. Required with runtime_version; conflicts require a fresh read.
   */
  expectedRuntimeVersion?: ExpectedRuntimeVersion | undefined;
  /**
   * The assistant ID.
   */
  id: string;
  /**
   * The system instructions for the assistant.
   */
  instructions?: string | undefined;
  /**
   * Maximum active warm runtimes.
   */
  maxConcurrency?: number | undefined;
  /**
   * MCP servers to attach. When sent, replaces the assistant's servers that are not hosted (toolset-backed). A hosted server listed here is attached if it is not already; attached hosted servers are left unchanged, and toolsets governs them when both are sent.
   */
  mcpServers?: Array<AssistantMCPServerRef> | undefined;
  /**
   * The model identifier used by the assistant.
   */
  model?: string | undefined;
  /**
   * The assistant name.
   */
  name?: string | undefined;
  /**
   * Desired runtime protocol: 2 for the default runtime, 3 for Kit. Switching requires the assistant and all background work to be settled.
   */
  runtimeVersion?: UpdateAssistantFormRuntimeVersion | undefined;
  /**
   * The assistant status.
   */
  status?: UpdateAssistantFormStatus | undefined;
  /**
   * Hosted (toolset-backed) MCP servers to attach, by toolset slug. When sent, replaces the assistant's hosted servers.
   */
  toolsets?: Array<AssistantToolsetRef> | undefined;
  /**
   * Warm runtime TTL in seconds.
   */
  warmTtlSeconds?: number | undefined;
};

/** @internal */
export const ExpectedRuntimeVersion$outboundSchema: z.ZodMiniEnum<
  typeof ExpectedRuntimeVersion
> = z.enum(ExpectedRuntimeVersion);

/** @internal */
export const UpdateAssistantFormRuntimeVersion$outboundSchema: z.ZodMiniEnum<
  typeof UpdateAssistantFormRuntimeVersion
> = z.enum(UpdateAssistantFormRuntimeVersion);

/** @internal */
export const UpdateAssistantFormStatus$outboundSchema: z.ZodMiniEnum<
  typeof UpdateAssistantFormStatus
> = z.enum(UpdateAssistantFormStatus);

/** @internal */
export type UpdateAssistantForm$Outbound = {
  expected_runtime_version?: number | undefined;
  id: string;
  instructions?: string | undefined;
  max_concurrency?: number | undefined;
  mcp_servers?: Array<AssistantMCPServerRef$Outbound> | undefined;
  model?: string | undefined;
  name?: string | undefined;
  runtime_version?: number | undefined;
  status?: string | undefined;
  toolsets?: Array<AssistantToolsetRef$Outbound> | undefined;
  warm_ttl_seconds?: number | undefined;
};

/** @internal */
export const UpdateAssistantForm$outboundSchema: z.ZodMiniType<
  UpdateAssistantForm$Outbound,
  UpdateAssistantForm
> = z.pipe(
  z.object({
    expectedRuntimeVersion: z.optional(ExpectedRuntimeVersion$outboundSchema),
    id: z.string(),
    instructions: z.optional(z.string()),
    maxConcurrency: z.optional(z.int()),
    mcpServers: z.optional(z.array(AssistantMCPServerRef$outboundSchema)),
    model: z.optional(z.string()),
    name: z.optional(z.string()),
    runtimeVersion: z.optional(
      UpdateAssistantFormRuntimeVersion$outboundSchema,
    ),
    status: z.optional(UpdateAssistantFormStatus$outboundSchema),
    toolsets: z.optional(z.array(AssistantToolsetRef$outboundSchema)),
    warmTtlSeconds: z.optional(z.int()),
  }),
  z.transform((v) => {
    return remap$(v, {
      expectedRuntimeVersion: "expected_runtime_version",
      maxConcurrency: "max_concurrency",
      mcpServers: "mcp_servers",
      runtimeVersion: "runtime_version",
      warmTtlSeconds: "warm_ttl_seconds",
    });
  }),
);

export function updateAssistantFormToJSON(
  updateAssistantForm: UpdateAssistantForm,
): string {
  return JSON.stringify(
    UpdateAssistantForm$outboundSchema.parse(updateAssistantForm),
  );
}
